import { useEffect, useState, useCallback } from "react";
import { Copy, Check, Monitor, Smartphone, ExternalLink, Radio, Code2, Eye, Image, Type, Maximize2, Palette, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { BACKEND_URL } from "../../context/AuthContext";

const WIDGET_STYLES = [
  {
    id: "cover",
    label: "Standard player with cover",
    desc: "Shows album art, now playing info, and station name. Best for sidebars and blog posts.",
    icon: Image,
    width: "560px",
    height: "140px",
  },
  {
    id: "compact",
    label: "Standard player without cover",
    desc: "Compact player with now playing info. Great for footers and narrow spaces.",
    icon: Type,
    width: "400px",
    height: "100px",
  },
  {
    id: "fullwidth",
    label: "Full width player",
    desc: "Slim bar player that spans the full width. Perfect for headers and navigation bars.",
    icon: Maximize2,
    width: "100%",
    height: "56px",
  },
];

const PRESET_COLORS = [
  "#8B5CF6", "#6366F1", "#3B82F6", "#06B6D4", "#10B981",
  "#22C55E", "#EAB308", "#F97316", "#EF4444", "#EC4899",
  "#A855F7", "#14B8A6", "#F43F5E", "#0EA5E9", "#84CC16",
];

export const StandardWidgetTab = ({ currentUser, authToken }) => {
  const [apiKey, setApiKey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copiedBtn, setCopiedBtn] = useState("");
  const [previewStyle, setPreviewStyle] = useState("cover");
  const [previewDevice, setPreviewDevice] = useState("desktop");
  const [customColor, setCustomColor] = useState("");
  const [customAccent, setCustomAccent] = useState("");
  const [defaultColor, setDefaultColor] = useState("#8B5CF6");
  const [defaultAccent, setDefaultAccent] = useState("#22C55E");
  const [iframeKeys, setIframeKeys] = useState({});

  const headers = useCallback(() => {
    const h = { "Content-Type": "application/json" };
    if (authToken) h["Authorization"] = `Bearer ${authToken}`;
    return h;
  }, [authToken]);

  useEffect(() => {
    setLoading(true);
    fetch(`${BACKEND_URL}/api/embed/my-key`, { headers: headers() })
      .then((r) => r.json())
      .then((data) => {
        if (data.has_key) setApiKey(data.api_key);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [headers]);

  // Fetch whitelabel defaults
  useEffect(() => {
    if (!authToken) return;
    fetch(`${BACKEND_URL}/api/whitelabel/config`, { headers: headers() })
      .then((r) => r.ok ? r.json() : null)
      .then((data) => {
        if (data?.config) {
          setDefaultColor(data.config.primary_color || "#8B5CF6");
          setDefaultAccent(data.config.secondary_color || "#22C55E");
        }
      })
      .catch(() => {});
  }, [authToken, headers]);

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
    setCopiedBtn(label);
    setTimeout(() => setCopiedBtn(""), 2000);
  };

  const siteUrl = "https://djsandmcmedia.com";

  const buildColorParams = () => {
    const params = [];
    if (customColor) params.push(`color=${encodeURIComponent(customColor)}`);
    if (customAccent) params.push(`accent=${encodeURIComponent(customAccent)}`);
    return params.length ? `&${params.join("&")}` : "";
  };

  const getEmbedCode = (styleId) => {
    if (!apiKey) return "";
    const s = WIDGET_STYLES.find((w) => w.id === styleId);
    const iframeWidth = s.id === "fullwidth" ? "100%" : s.width;
    const colorParams = buildColorParams();
    return `<iframe src="${siteUrl}/api/embed/${apiKey}/widget?style=${styleId}${colorParams}" style="width:${iframeWidth};height:${s.height};border:none;border-radius:${s.id === 'fullwidth' ? '0' : '12px'};overflow:hidden" allow="autoplay" loading="lazy"></iframe>`;
  };

  const getPreviewUrl = (styleId) => {
    if (!apiKey) return "";
    const colorParams = buildColorParams();
    return `${BACKEND_URL}/api/embed/${apiKey}/widget?style=${styleId}${colorParams}`;
  };

  const refreshPreviews = () => {
    const keys = {};
    WIDGET_STYLES.forEach(s => { keys[s.id] = Date.now(); });
    keys.live = Date.now();
    setIframeKeys(keys);
  };

  const handleColorChange = (color) => {
    setCustomColor(color);
    refreshPreviews();
  };

  const handleAccentChange = (accent) => {
    setCustomAccent(accent);
    refreshPreviews();
  };

  const resetColors = () => {
    setCustomColor("");
    setCustomAccent("");
    refreshPreviews();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="widget-tab-loading">
        <Radio className="w-6 h-6 text-purple-400 animate-spin" />
      </div>
    );
  }

  if (!apiKey) {
    return (
      <div className="space-y-6" data-testid="widget-tab-no-key">
        <div className="bg-gradient-to-br from-purple-500/10 via-indigo-600/5 to-transparent border border-purple-500/20 rounded-2xl p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-purple-500/10 flex items-center justify-center mx-auto mb-4">
            <Radio className="w-8 h-8 text-purple-400" />
          </div>
          <h2 className="text-xl font-bold text-white mb-2">Generate an API Key First</h2>
          <p className="text-white/40 text-sm max-w-md mx-auto">
            You need an Embed API key to create widget players. Go to the <strong className="text-white/60">Embed Hub</strong> tab to generate one.
          </p>
        </div>
      </div>
    );
  }

  const activeColor = customColor || defaultColor;
  const activeAccent = customAccent || defaultAccent;

  return (
    <div className="space-y-8" data-testid="widget-tab">
      {/* Hero */}
      <div className="bg-gradient-to-br from-cyan-500/10 via-blue-600/5 to-transparent border border-cyan-500/20 rounded-2xl p-6 md:p-8">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
            <Radio className="w-6 h-6 text-cyan-400" />
          </div>
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-white mb-1" data-testid="widget-hero-title">
              Standard Widget Players
            </h2>
            <p className="text-white/40 text-sm leading-relaxed max-w-lg">
              Lightweight embeddable players with live now-playing data. Customize colors and copy the embed code into any website.
            </p>
          </div>
        </div>
      </div>

      {/* Color Picker */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-6" data-testid="widget-color-picker">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Palette className="w-5 h-5 text-cyan-400" />
            <h3 className="text-base font-bold text-white">Widget Colors</h3>
          </div>
          {(customColor || customAccent) && (
            <button
              onClick={resetColors}
              className="flex items-center gap-1.5 text-white/40 hover:text-white/60 text-xs transition"
              data-testid="widget-reset-colors"
            >
              <RotateCcw className="w-3 h-3" /> Reset to defaults
            </button>
          )}
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Primary Color */}
          <div>
            <label className="text-white/50 text-xs font-medium mb-2 block">Primary Color</label>
            <div className="flex items-center gap-3 mb-3">
              <div
                className="w-10 h-10 rounded-lg border-2 border-white/20 flex-shrink-0 cursor-pointer relative overflow-hidden"
                style={{ backgroundColor: activeColor }}
                data-testid="widget-primary-swatch"
              >
                <input
                  type="color"
                  value={activeColor}
                  onChange={(e) => handleColorChange(e.target.value)}
                  className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                  data-testid="widget-primary-input"
                />
              </div>
              <input
                type="text"
                value={customColor || defaultColor}
                onChange={(e) => {
                  const v = e.target.value;
                  if (/^#[0-9a-fA-F]{0,6}$/.test(v)) {
                    setCustomColor(v);
                    if (/^#[0-9a-fA-F]{6}$/.test(v)) refreshPreviews();
                  }
                }}
                placeholder={defaultColor}
                className="bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-white text-sm font-mono w-28"
                data-testid="widget-primary-hex"
              />
              <span className="text-white/30 text-xs">Buttons, accents, EQ bars</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {PRESET_COLORS.map((c) => (
                <button
                  key={c}
                  onClick={() => handleColorChange(c)}
                  className={`w-6 h-6 rounded-md border transition hover:scale-110 ${activeColor === c ? 'border-white scale-110' : 'border-white/10'}`}
                  style={{ backgroundColor: c }}
                  data-testid={`widget-preset-${c.slice(1)}`}
                />
              ))}
            </div>
          </div>

          {/* Accent Color */}
          <div>
            <label className="text-white/50 text-xs font-medium mb-2 block">Accent Color</label>
            <div className="flex items-center gap-3 mb-3">
              <div
                className="w-10 h-10 rounded-lg border-2 border-white/20 flex-shrink-0 cursor-pointer relative overflow-hidden"
                style={{ backgroundColor: activeAccent }}
                data-testid="widget-accent-swatch"
              >
                <input
                  type="color"
                  value={activeAccent}
                  onChange={(e) => handleAccentChange(e.target.value)}
                  className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                  data-testid="widget-accent-input"
                />
              </div>
              <input
                type="text"
                value={customAccent || defaultAccent}
                onChange={(e) => {
                  const v = e.target.value;
                  if (/^#[0-9a-fA-F]{0,6}$/.test(v)) {
                    setCustomAccent(v);
                    if (/^#[0-9a-fA-F]{6}$/.test(v)) refreshPreviews();
                  }
                }}
                placeholder={defaultAccent}
                className="bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-white text-sm font-mono w-28"
                data-testid="widget-accent-hex"
              />
              <span className="text-white/30 text-xs">Gradients, top bar</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {PRESET_COLORS.map((c) => (
                <button
                  key={`a-${c}`}
                  onClick={() => handleAccentChange(c)}
                  className={`w-6 h-6 rounded-md border transition hover:scale-110 ${activeAccent === c ? 'border-white scale-110' : 'border-white/10'}`}
                  style={{ backgroundColor: c }}
                  data-testid={`widget-accent-preset-${c.slice(1)}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Widget Styles Grid */}
      {WIDGET_STYLES.map((style) => {
        const Icon = style.icon;
        const embedCode = getEmbedCode(style.id);
        const previewUrl = getPreviewUrl(style.id);
        const copyId = `embed-${style.id}`;

        return (
          <div
            key={style.id}
            className="bg-white/5 border border-white/10 rounded-2xl p-6"
            data-testid={`widget-style-${style.id}`}
          >
            <div className="flex items-center gap-3 mb-1">
              <div className="w-9 h-9 rounded-lg bg-cyan-500/15 flex items-center justify-center flex-shrink-0">
                <Icon className="w-4 h-4 text-cyan-400" />
              </div>
              <h3 className="text-base font-bold text-white">{style.label}</h3>
            </div>
            <p className="text-white/40 text-xs mb-4 ml-12">{style.desc}</p>

            {/* Preview */}
            <div className={`bg-black/40 rounded-xl overflow-hidden mb-4 ${style.id === 'fullwidth' ? '' : 'mx-auto'}`}
              style={{ maxWidth: style.id === 'fullwidth' ? '100%' : style.width }}>
              <iframe
                key={iframeKeys[style.id] || style.id}
                src={previewUrl}
                title={`${style.label} Preview`}
                style={{
                  width: "100%",
                  height: style.height,
                  border: "none",
                  borderRadius: style.id === "fullwidth" ? 0 : "12px",
                }}
                allow="autoplay"
                data-testid={`widget-preview-${style.id}`}
              />
            </div>

            {/* Embed Code */}
            <p className="text-white/30 text-[10px] font-medium mb-1 flex items-center gap-1.5">
              <Code2 className="w-3 h-3" /> Copy and paste this code in your website
            </p>
            <div className="relative">
              <pre className="bg-black/60 border border-white/10 rounded-xl p-4 text-cyan-400 text-xs overflow-x-auto font-mono whitespace-pre-wrap break-all pr-14">
                {embedCode}
              </pre>
              <button
                onClick={() => copyToClipboard(embedCode, copyId)}
                className="absolute top-3 right-3 bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-400 px-3 py-2 rounded-lg transition flex items-center gap-1.5 text-xs font-medium"
                data-testid={`widget-copy-${style.id}`}
              >
                {copiedBtn === copyId ? (
                  <><Check className="w-3.5 h-3.5" /> Copied</>
                ) : (
                  <><Copy className="w-3.5 h-3.5" /> Copy</>
                )}
              </button>
            </div>
          </div>
        );
      })}

      {/* Live Preview Section */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-6" data-testid="widget-live-preview">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Eye className="w-5 h-5 text-cyan-400" />
            <h3 className="text-base font-bold text-white">Live Preview</h3>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 bg-white/5 rounded-lg p-1">
              {WIDGET_STYLES.map((s) => (
                <button
                  key={s.id}
                  onClick={() => setPreviewStyle(s.id)}
                  className={`px-3 py-1.5 rounded-md text-xs transition ${
                    previewStyle === s.id ? "bg-cyan-500/20 text-cyan-400" : "text-white/40 hover:text-white"
                  }`}
                  data-testid={`widget-preview-tab-${s.id}`}
                >
                  {s.id === "cover" ? "Cover" : s.id === "compact" ? "Compact" : "Full"}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-1 bg-white/5 rounded-lg p-1">
              <button
                onClick={() => setPreviewDevice("desktop")}
                className={`p-2 rounded-md transition ${
                  previewDevice === "desktop" ? "bg-cyan-500/20 text-cyan-400" : "text-white/40 hover:text-white"
                }`}
                data-testid="widget-preview-desktop"
              >
                <Monitor className="w-4 h-4" />
              </button>
              <button
                onClick={() => setPreviewDevice("mobile")}
                className={`p-2 rounded-md transition ${
                  previewDevice === "mobile" ? "bg-cyan-500/20 text-cyan-400" : "text-white/40 hover:text-white"
                }`}
                data-testid="widget-preview-mobile"
              >
                <Smartphone className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        <div
          className={`bg-black/40 rounded-xl overflow-hidden mx-auto transition-all ${
            previewDevice === "mobile" ? "max-w-[375px]" : "max-w-full"
          }`}
        >
          <iframe
            key={iframeKeys.live || "live"}
            src={getPreviewUrl(previewStyle)}
            className="w-full border-0"
            style={{
              height: WIDGET_STYLES.find((s) => s.id === previewStyle)?.height || "120px",
              borderRadius: previewStyle === "fullwidth" ? 0 : "12px",
            }}
            title="Widget Preview"
            allow="autoplay"
            data-testid="widget-live-preview-iframe"
          />
        </div>

        <div className="mt-4 flex items-center justify-center gap-4">
          <a
            href={getPreviewUrl(previewStyle)}
            target="_blank"
            rel="noopener noreferrer"
            className="text-cyan-400 hover:text-cyan-300 text-xs flex items-center gap-1 transition"
            data-testid="widget-open-preview"
          >
            <ExternalLink className="w-3.5 h-3.5" /> Open in new tab
          </a>
          <button
            onClick={() => copyToClipboard(getEmbedCode(previewStyle), "preview")}
            className="text-cyan-400 hover:text-cyan-300 text-xs flex items-center gap-1 transition"
            data-testid="widget-copy-preview"
          >
            {copiedBtn === "preview" ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
            Copy embed code
          </button>
        </div>
      </div>
    </div>
  );
};
